/* Lightweight generic list+create form. Used by Routes/Shifts/Subjects/Templates/Affidavits/GLBA. */
import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Drawer, Field } from "./UI";
import { Plus } from "@phosphor-icons/react";

/**
 * Props:
 *   endpoint:    string  — e.g. "/routes"
 *   columns:     [{key, label, render?}]
 *   formFields:  [{key, label, type, options?}]
 *   testid:      string  — page-level data-testid prefix
 *   adminWrites: bool
 *   defaultForm: object
 */
export default function CrudPanel({ endpoint, columns, formFields, testid, defaultForm = {}, adminWrites = false, rowActions = null, onItemsLoaded = null }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState(null);
  // iter 37 — Bulk-select groundwork. Stores ids of selected rows; bulk
  // actions are not implemented yet, this is the foundation only.
  const [selected, setSelected] = useState(() => new Set());

  const isWide = useMemo(() => formFields.some(f => f.type === "templateBody"), [formFields]);

  const load = () => api.get(endpoint).then(r => {
    setItems(r.data);
    setSelected(new Set());
    if (onItemsLoaded) onItemsLoaded(r.data);
  }).catch(e => setErr(formatErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [endpoint]);

  const openEdit = (it) => {
    // Hydrate the form with the row's values so the drawer acts as the
    // "most detailed view" — see iter 37 list-page UX spec.
    const seed = {};
    formFields.forEach(f => { seed[f.key] = it[f.key] ?? ""; });
    setForm(seed); setEditingId(it.id); setErr(""); setOpen(true);
  };

  const submit = async () => {
    setErr("");
    try {
      // coerce empty strings to null and numeric fields
      const body = {};
      formFields.forEach(f => {
        const v = form[f.key];
        if (v === "" || v === undefined) { body[f.key] = null; return; }
        if (f.type === "number") body[f.key] = parseFloat(v);
        else if (f.type === "boolean") body[f.key] = !!v;
        else if (f.type === "date" || f.type === "datetime-local") body[f.key] = new Date(v).toISOString();
        else body[f.key] = v;
      });
      if (editingId) await api.put(`${endpoint}/${editingId}`, body);
      else await api.post(endpoint, body);
      setOpen(false); setForm(defaultForm); setEditingId(null); load();
    } catch (e) { setErr(formatErr(e)); }
  };

  // Bulk-select toggles
  const toggleOne = (id) => setSelected(s => {
    const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const allSelected = items.length > 0 && items.every(it => selected.has(it.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map(i => i.id)));

  return (
    <>
      <div className="flex justify-end mb-4">
        <button className="btn btn-primary" onClick={() => { setForm(defaultForm); setEditingId(null); setErr(""); setOpen(true); }} data-testid={`new-${testid}-btn`}>
          <Plus size={14} /> NEW
        </button>
      </div>

      <div className="surface" data-testid={`${testid}-table`}>
        {items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm" data-testid={`empty-${testid}`}>
            No items yet. Click NEW to add one.
          </div>
        ) : (
          <table className="dense">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    data-testid={`${testid}-select-all`}
                    aria-label="Select all"
                  />
                </th>
                {columns.map(c => <th key={c.key}>{c.label}</th>)}
                {rowActions && <th></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} data-testid={`${testid}-row-${it.id}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(it.id)}
                      onChange={() => toggleOne(it.id)}
                      data-testid={`${testid}-select-${it.id}`}
                      aria-label={`Select ${it.id}`}
                    />
                  </td>
                  {columns.map((c, ci) => (
                    <td key={c.key} className={c.numeric ? "num mono" : ""}>
                      {ci === 0 ? (
                        // First column = clickable link → opens most detailed view (edit drawer).
                        <button
                          className="link-cell"
                          onClick={() => openEdit(it)}
                          data-testid={`${testid}-open-${it.id}`}
                          title="Open detailed view"
                        >
                          {c.render ? c.render(it) : (it[c.key] ?? "—")}
                        </button>
                      ) : (
                        c.render ? c.render(it) : (it[c.key] ?? "—")
                      )}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="text-right">
                      {rowActions(it)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} size={isWide ? "wide" : "narrow"} testid={`${testid}-drawer`} title={editingId ? "Edit item" : "New item"}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setOpen(false)}>CANCEL</button>
            <button className="btn btn-primary" onClick={submit} data-testid={`save-${testid}-btn`}>
              <Plus size={14} /> {editingId ? "SAVE" : "CREATE"}
            </button>
          </>
        }>
        {formFields.filter(f => f.type !== "hidden").map(f => (
          <Field key={f.key} label={f.label}>
            {f.type === "select" ? (
              <select className="input" value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-${f.key}`}>
                <option value="">—</option>
                {(f.options || []).map(o => <option key={o.value || o.id} value={o.value || o.id}>{o.label || o.name}</option>)}
              </select>
            ) : f.type === "textarea" ? (
              <textarea className="input" rows={3} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-${f.key}`} />
            ) : f.type === "templateBody" ? (
              <TemplateBodyField
                value={form[f.key] || ""}
                onChange={(v) => setForm({ ...form, [f.key]: v })}
                testid={`${testid}-${f.key}`}
              />
            ) : f.type === "boolean" ? (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })} data-testid={`${testid}-${f.key}`} />
                <span>{f.label}</span>
              </label>
            ) : (
              <input type={f.type || "text"} className="input" value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-${f.key}`} />
            )}
          </Field>
        ))}
        {err && <div className="text-xs text-destructive mt-2" data-testid={`${testid}-error`}>{err}</div>}
      </Drawer>
    </>
  );
}

/** Two-pane editor: textarea on the left, live-interpolated preview on the right.
 *  Placeholders detected from {{key}} patterns. Sample values are editable below. */
const DEFAULT_SAMPLES = {
  date: "January 15, 2026",
  time: "2:30 PM",
  name: "John Q. Smith",
  first_name: "John",
  last_name: "Smith",
  address: "123 Main Street, Austin, TX 78701",
  case_number: "D-1-GN-26-001234",
  plaintiff: "ACME Corp.",
  defendant: "John Q. Smith",
  judge: "Hon. K. Brown",
  county: "Travis County",
  state: "Texas",
  process_server: "Jane Smith, License #12345",
  job_number: "JOB-2026-00042",
  attempt: "1st",
};

function TemplateBodyField({ value, onChange, testid }) {
  const [samples, setSamples] = useState(DEFAULT_SAMPLES);
  const placeholders = useMemo(() => {
    const set = new Set();
    const re = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
    let m;
    while ((m = re.exec(value)) !== null) set.add(m[1].toLowerCase());
    return Array.from(set);
  }, [value]);

  const rendered = useMemo(() => {
    return value.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_, k) => {
      const key = k.toLowerCase();
      const v = samples[key];
      return v !== undefined && v !== "" ? v : `[${key}]`;
    });
  }, [value, samples]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid={testid}>
      <div>
        <div className="label-xs mb-1">SOURCE</div>
        <textarea
          className="input font-mono"
          rows={14}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="On {{date}}, I served {{name}} at {{address}}..."
          data-testid={`${testid}-source`}
          spellCheck={false}
        />
        {placeholders.length > 0 && (
          <div className="mt-3">
            <div className="label-xs mb-1">SAMPLE VALUES ({placeholders.length} placeholder{placeholders.length === 1 ? "" : "s"})</div>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto" data-testid={`${testid}-samples`}>
              {placeholders.map((k) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <span className="mono text-muted-foreground min-w-[110px] truncate" title={`{{${k}}}`}>{`{{${k}}}`}</span>
                  <input
                    className="input"
                    value={samples[k] ?? ""}
                    onChange={(e) => setSamples({ ...samples, [k]: e.target.value })}
                    placeholder={`sample for ${k}`}
                    data-testid={`${testid}-sample-${k}`}
                  />
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      <div>
        <div className="label-xs mb-1">LIVE PREVIEW</div>
        <div
          className="surface p-4 text-sm whitespace-pre-wrap min-h-[330px] max-h-[560px] overflow-y-auto"
          data-testid={`${testid}-preview`}
        >
          {value ? rendered : <span className="text-muted-foreground italic">Start typing in the source pane to see a live preview…</span>}
        </div>
        {placeholders.length === 0 && value && (
          <div className="text-xs text-muted-foreground mt-2">
            No <code className="mono">{`{{placeholders}}`}</code> detected — try adding e.g. <code className="mono">{`{{name}}`}</code>, <code className="mono">{`{{date}}`}</code>.
          </div>
        )}
      </div>
    </div>
  );
}
